import { ChangeEvent, useEffect } from "react";

import { Button, Checkbox, LineClamp, useModal } from "@newsela/angelou";
import { withTrackEvent, objectTypes, actionNames } from "@newsela/snitch";

import { useSearchAdminPermissions } from "@/search/Langston/useSearchAdminPermissions";
import { useSearchPageContext } from "@/search/Langston/SearchPageProvider";
import { LangstonParameterSchema } from "@/search/Langston/LangstonParameterSchema";
import { useStandardsSearchContext } from "@/search/Langston/StandardsSearchProvider";
import { FilterSection } from "../../FilterSection";
import { StandardsModalProvider } from "./StandardsModalProvider";
import { StandardsModal } from "./StandardsModal";
import {
  checkboxCss,
  listContainerCss,
  listItemCss,
  rootCss,
  standardDescriptionCss,
} from "./Standards.styles";

const CheckboxWithTrackEvent = withTrackEvent(Checkbox);
const ButtonWithTrackEvent = withTrackEvent(Button);

const standardsQueryParam = LangstonParameterSchema.standards.key;

export const Standards = () => {
  const {
    getters: { isDisabledFilters, viewAllStandards },
    handlers: {
      getSearchParamValues,
      hasSearchParamValue,
      toggleSearchParamValue,
    },
  } = useSearchPageContext();

  const {
    top5Standards,
    updateSelectedStandardsOnChange,
    isLoadingStandardsResults,
  } = useStandardsSearchContext();

  const standardsInQuery = getSearchParamValues(standardsQueryParam);

  const [standardsModalProps, setStandardsModalActive] = useModal(false);
  const { blockActionAndShowConfirmationModal, blockFilterChanges } =
    useSearchAdminPermissions();

  useEffect(() => {
    if (viewAllStandards) {
      setStandardsModalActive(true);
    }
  }, [viewAllStandards, setStandardsModalActive]);

  if (!top5Standards) return null;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const standardParsed = JSON.parse(e.target.value);
    const standardContentId = standardParsed.id;

    if (blockFilterChanges) {
      blockActionAndShowConfirmationModal();
      return;
    }

    updateSelectedStandardsOnChange(e.target.checked, standardParsed, true);
    toggleSearchParamValue(standardsQueryParam, standardContentId);
  };

  return (
    <FilterSection
      title="Standards"
      defaultCollapsed={!standardsInQuery.length}
    >
      <div css={rootCss}>
        <ul css={listContainerCss}>
          {top5Standards?.map((standard) => {
            const standardTitle = `${standard.name}`;

            return (
              <li key={standard.id} css={listItemCss}>
                <CheckboxWithTrackEvent
                  __cssFor={checkboxCss}
                  dataProps={{
                    "data-qa-selector": "filter-by-standard-checkbox",
                  }}
                  label={standardTitle}
                  value={JSON.stringify(standard)}
                  disabled={isDisabledFilters}
                  checked={hasSearchParamValue(
                    standardsQueryParam,
                    standard.id,
                  )}
                  onChange={handleChange}
                  objectType={objectTypes.content}
                  actionName={actionNames.filter}
                  streamProperties={{
                    description: "Select standard for search query",
                  }}
                />
                <LineClamp
                  lines={2}
                  text={standard.description || ""}
                  innerElement="p"
                  css={standardDescriptionCss}
                />
              </li>
            );
          })}
        </ul>
        <ButtonWithTrackEvent
          onClick={() => setStandardsModalActive(true)}
          dataProps={{
            "data-qa-selector": "open-view-all-standard-modal-button",
          }}
          disabled={isLoadingStandardsResults || isDisabledFilters}
          flavor={Button.flavors.tertiary}
          objectType={objectTypes.content}
          actionName={actionNames.filter}
          streamProperties={{
            description: "View all standards modal for search querying",
          }}
        >
          View all standards
        </ButtonWithTrackEvent>
        <StandardsModalProvider>
          <StandardsModal
            modalProps={standardsModalProps}
            setStandardsModalActive={setStandardsModalActive}
          />
        </StandardsModalProvider>
      </div>
    </FilterSection>
  );
};
